import React, { useEffect, useState } from 'react'
import SEO from 'src/components/common/SEO'
import HeroLiveFeed from 'src/components/common/HeroLivePlayer'
import { useSelector } from 'react-redux'
import { selectParams } from 'src/modules/reducers/params'
import { selectCountryCode } from 'src/modules/reducers/market'
import { Helmet } from 'react-helmet'
import {
  ChannelSection,
  DealSection,
  FAQSection,
  HighlightSection,
  Layout,
  NetworkSection,
  PlatformSection,
} from 'src/components/common'
import { PlanCardsWithTabs } from 'src/components/common/PlanCardsWithTabs'
import { useIsTrial } from 'src/modules/hooks/useIsTrial'
import HeaderSignIn from 'src/components/common/HeaderSignIn'
import { WEB_MARKETING_HEADER_SIGN_IN } from 'src/modules/reducers/cohorts/constants'
import { useCohortFlag } from 'src/modules/hooks/useCohortFlag'

const CanadaLiveTemplateDefault = ({
  location,
  lang = 'en',
  market = 'canada',
  ctaHrefOverride,
  // Offer Code
  offerCode,
  offerBanner,
  // Metadata
  pageTitle,
  pageDescription,
  // Hero Section
  logo,
  logoHeight,
  title,
  subtitle,
  ctaText,
  background,
  callsign,
  // Optional Highlight Section
  highlightImage,
  highlightHeadline,
  highlightSubtitle,
  highlightNote,
  highlightBackground,
  // Pass Network Name to Live Preview
  networkName,
  // Plan Cards
  hasPlanCardsTabs = true,
  planCardsConfig = {},
  plansTitle,
  plansSubtitle,

  // Network Section
  networkHeadline,
  networkSubtitle,
  networkSet = 'canada',
  customNetworks,
  // Others
  team,
  forceAnchorLink,
  // MLB.TV Offer
  mlbtvOffer,
  // Plan Card Override
  hasOfferInput,
  // Personalization Params
  sportId,
  leagueId,
  teamId,
  disableFreeTrial,
  additionalFAQs,
  faqListOverride,
}) => {
  const isTrial = useIsTrial(market)

  let metaDescription =
    pageDescription ||
    // TEMPORARY NBC DISPUTE
    // '200+ live channels. Free Cloud DVR. Watch on your phone, Roku, Apple TV, Amazon Fire TV, computer & more. FOX, NBC, CBS, NFL Network, SHOWTIME, beIN SPORTS & more.'
    '200+ live channels. Free Cloud DVR. Watch on your phone, Roku, Apple TV, Amazon Fire TV, computer & more. FOX, CBS, NFL Network, SHOWTIME, beIN SPORTS & more.'

  const params = useSelector(selectParams)
  const countryCode = useSelector(selectCountryCode)
  const showHeaderSignIn = useCohortFlag(WEB_MARKETING_HEADER_SIGN_IN)
  disableFreeTrial = disableFreeTrial ?? !isTrial

  const [pageSlug, setPageSlug] = useState(null)
  useEffect(() => {
    if (callsign) {
      setPageSlug(callsign)
    } else if (countryCode === 'CA' || countryCode === 'CAN') {
      setPageSlug('BEINCHD')
    } else if (params?.page_slug) {
      setPageSlug(params?.page_slug)
    } else {
      setPageSlug('FS1')
    }
  }, [params?.page_slug, countryCode])

  const isAnchor = forceAnchorLink && hasPlanCardsTabs ? '#select-plan' : null
  return (
    <Layout
      lang={lang}
      market={market}
      offerCode={offerCode}
      offerBanner={offerBanner}
      ctaHrefOverride={isAnchor}
      sportId={sportId}
      leagueId={leagueId}
      teamId={teamId}
    >
      <Helmet>
        <link rel="preconnect" crossOrigin="anonymous" href="https://preview.fubo.tv" />
        <link rel="dns-prefetch" crossOrigin="anonymous" href="https://preview.fubo.tv" />
      </Helmet>
      <SEO lang={lang} market={market} title={pageTitle || title} description={metaDescription} />
      {showHeaderSignIn && (
        <HeaderSignIn
          lang={lang}
          market={market}
          disableFreeTrial={disableFreeTrial}
          ctaText={ctaText}
        />
      )}
      <HeroLiveFeed
        lang={lang}
        market={market}
        callsign={pageSlug}
        title={title}
        subTitle={subtitle}
        background={background}
        ctaText={ctaText}
        subhead="Keep watching with Fubo."
        logo={logo}
        logoHeight={logoHeight}
        networkName={networkName}
        ctaHrefOverride={isAnchor}
        disableFreeTrial={disableFreeTrial}
      />
      {highlightImage && (
        <HighlightSection
          image={highlightImage}
          title={highlightHeadline}
          subtitle={highlightSubtitle}
          miceText={highlightNote}
          background={highlightBackground}
          ctaHrefOverride={isAnchor}
          market={market}
          lang={lang}
        />
      )}

      {hasPlanCardsTabs && (
        <PlanCardsWithTabs
          lang={lang}
          market={market}
          ctaText={ctaText}
          plans={planCardsConfig.plans}
          tabs={planCardsConfig.tabs}
          activeTab={planCardsConfig.activeTab}
          overrides={planCardsConfig.overrides}
          sharedData={planCardsConfig.sharedData}
          title={plansTitle || 'Which plan is right for you?'}
          hideTabs={planCardsConfig.hideTabs}
          displayMode={planCardsConfig.displayMode}
          subtitle={
            !!plansSubtitle
              ? plansSubtitle
              : "Canada's home for streaming live soccer and top entertainment"
          }
        />
      )}
      <NetworkSection
        lang={lang}
        market={market}
        headline={networkHeadline}
        subtitle={networkSubtitle}
        networkSet={networkSet}
        customNetworks={customNetworks}
        ctaText={ctaText}
        ctaHrefOverride={isAnchor}
        disableFreeTrial={disableFreeTrial}
      />
      <ChannelSection
        lang={lang}
        market={market}
        showAllChannels={true}
        title="Your Fubo channel lineup"
      />
      <DealSection lang={lang} market={market} />
      <PlatformSection
        lang={lang}
        market={market}
        ctaText={ctaText}
        ctaHrefOverride={isAnchor}
        disableFreeTrial={disableFreeTrial}
      />
      <FAQSection
        lang={lang}
        market={market}
        team={team}
        additionalFAQs={additionalFAQs}
        faqListOverride={faqListOverride}
      />
    </Layout>
  )
}

export default CanadaLiveTemplateDefault
